import * as React from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Info,
  XCircle,
  type LucideIcon,
} from "lucide-react";

import { Badge, type BadgeProps } from "@/components/ui/badge";

/**
 * TINA StatusPill — Badge + icon, tone matched to MASTER.md semantic colors.
 *
 * Per design rule "color is never the only signal", every status uses
 * tone + icon + text together.
 */
type StatusKind =
  | "success"
  | "pending"
  | "warning"
  | "danger"
  | "info"
  | "deadline";

const ICON_MAP: Record<StatusKind, LucideIcon> = {
  success: CheckCircle2,
  pending: Clock,
  warning: Clock,
  danger: XCircle,
  info: Info,
  deadline: AlertTriangle,
};

const TONE_MAP: Record<StatusKind, BadgeProps["tone"]> = {
  success: "success",
  pending: "warning",
  warning: "warning",
  danger: "danger",
  info: "info",
  deadline: "accent", // amber — RESERVED for filing deadlines
};

interface StatusPillProps {
  kind: StatusKind;
  children: React.ReactNode;
  className?: string;
}

export function StatusPill({ kind, children, className }: StatusPillProps) {
  const Icon = ICON_MAP[kind];
  return (
    <Badge tone={TONE_MAP[kind]} className={className}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {children}
    </Badge>
  );
}
